/**
 * API Client - Centralized HTTP client for all API calls
 * 
 * Features:
 * - Automatic authentication handling
 * - Consistent error handling
 * - Type-safe responses
 * - Automatic redirects on 401
 * - Environment-based API URL configuration
 * 
 * Usage:
 * ```ts
 * const projects = await apiClient.get<Project[]>('/projects')
 * const newProject = await apiClient.post<Project>('/projects', data)
 * ```
 */

import { config } from '../config'

// Base API URL - configured per environment
const API_BASE_URL = config.apiBaseUrl

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public response?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ApiClientConfig {
  headers?: HeadersInit
  onUnauthorized?: () => void
  /**
   * SOLY-REGIE-006 — a 403 means « the permission map this session is holding is out of date ».
   *
   * Permissions are read once, at AuthProvider mount (`/auth/me`). Take a permission away in
   * /admin and the open tab keeps the old one: the route guard says yes, every call answers 403,
   * and the screen renders EMPTY. That is what a planning without rights actually looked like —
   * a cockpit that seemed to work and to have lost every chantier.
   *
   * So the 403 refreshes the session instead of being swallowed, and the route guard does the
   * rest. It is deliberately generic: the same silence existed for invoices, supplier orders and
   * settings.
   */
  onForbidden?: () => void
}

/**
 * Extends the standard `RequestInit` with Soly-specific options.
 *
 * `skipUnauthorizedHandler`: when true, a 401 response from this call
 * does NOT invoke `config.onUnauthorized`. Use this on probes that
 * legitimately expect a 401 (e.g. the `/auth/me` bootstrap call from
 * `getCurrentUser()` on public pages like /register, /verify, /reset).
 * Without this escape hatch, the handler would kick anonymous visitors
 * off those pages on mount — the original bug that led to the handler
 * being commented out in commit f4aebc5.
 */
export interface ApiRequestOptions extends RequestInit {
  skipUnauthorizedHandler?: boolean
  /**
   * Same escape hatch as above, for the 403 handler: the session refresh must not itself be able
   * to trigger a session refresh.
   */
  skipForbiddenHandler?: boolean
}

class ApiClient {
  private config: ApiClientConfig = {}

  configure(config: ApiClientConfig) {
    this.config = { ...this.config, ...config }
  }

  /**
   * Sends the request and applies the shared status handling (401 → onUnauthorized, 403 →
   * onForbidden, other errors → ApiError). Returns the response for the caller to read its body.
   */
  private async send(endpoint: string, options: ApiRequestOptions = {}): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`
    const { skipUnauthorizedHandler, skipForbiddenHandler, ...fetchOptions } = options

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...this.config.headers,
      ...fetchOptions.headers,
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include', // Important: Include cookies for session
      })

      // Handle 401 Unauthorized
      if (response.status === 401) {
        if (this.config.onUnauthorized && !skipUnauthorizedHandler) {
          this.config.onUnauthorized()
        }
        throw new ApiError(401, 'UNAUTHORIZED', 'Non authentifié')
      }

      // Handle 403 Forbidden — the permission map is stale, not the session (see onForbidden).
      if (response.status === 403 && this.config.onForbidden && !skipForbiddenHandler) {
        this.config.onForbidden()
      }

      // Handle other errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const error = new ApiError(
          response.status,
          errorData.error?.code || errorData.code || 'UNKNOWN_ERROR',
          errorData.error?.message || errorData.message || errorData.error || 'Une erreur est survenue',
          errorData
        )
        // Attach response for specific error handling
        ;(error as ApiError).response = { status: response.status, data: errorData }
        throw error
      }

      return response
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      // Network error or other fetch errors
      throw new ApiError(
        0,
        'NETWORK_ERROR',
        'Impossible de se connecter au serveur'
      )
    }
  }

  private async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const response = await this.send(endpoint, options)
    try {
      // Handle 204 No Content
      if (response.status === 204) {
        return undefined as T
      }

      const text = await response.text()
      if (!text) {
        return undefined as T
      }
      return JSON.parse(text) as T
    } catch {
      // Unreadable body: same answer as before the send/read split.
      throw new ApiError(0, 'NETWORK_ERROR', 'Impossible de se connecter au serveur')
    }
  }

  /**
   * A binary response (PDF, image) read as a Blob, with the same 401/403/error handling as the
   * JSON calls — an expired session still redirects to login. `data`, when given, is sent as JSON.
   */
  async blob(
    endpoint: string,
    options: ApiRequestOptions & { data?: unknown } = {}
  ): Promise<{ blob: Blob; headers: Headers }> {
    const { data, ...rest } = options
    const response = await this.send(endpoint, {
      ...rest,
      body: data !== undefined ? JSON.stringify(data) : rest.body,
    })
    try {
      return { blob: await response.blob(), headers: response.headers }
    } catch {
      throw new ApiError(0, 'NETWORK_ERROR', 'Impossible de se connecter au serveur')
    }
  }

  async get<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(
    endpoint: string,
    data: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async patch<T>(
    endpoint: string,
    data: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
